import { ErrorModal, HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import { Grid2, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	CATALOG_CUSTOMER_INITIAL_VALUES,
	CatalogCustomer,
} from "@/shared/models/CatalogCustomer";
import {
	deleteCatalogCustomer,
	upsertCatalogCustomer,
} from "@/shared/repositories/CatalogCustomerRepository";

type Props = {
	customer: CatalogCustomer | null;
	modal: "create" | "update" | "delete" | "";
	onHide: (shouldUpdate?: boolean) => void;
};

export const UpsertDeleteModal = ({ customer, modal, onHide }: Props) => {
	const [title, setTitle] = useState<string>("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [error, setError] = useState("");

	const successMessage = `The customer was ${modal}d successfully`;

	const { control, reset, handleSubmit } = useForm<CatalogCustomer>({});

	const queryClient = useQueryClient();

	const deleteMutation = useMutation({
		mutationFn: (customerId: number) => deleteCatalogCustomer(customerId),
		onSuccess: async (response) => {
			if (response.ok) {
				await queryClient.invalidateQueries({ queryKey: ["catalogCustomers"] });
				setNotificationMessage(successMessage);
				onHide(true);
			} else {
				setError(response.message);
			}
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const upsertMutation = useMutation({
		mutationFn: (data: CatalogCustomer) => upsertCatalogCustomer(data),
		onSuccess: async (response) => {
			if (response.ok) {
				await queryClient.invalidateQueries({ queryKey: ["catalogCustomers"] });
				setNotificationMessage(successMessage);
				onHide(true);
			} else {
				setError(response.message);
			}
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const onSubmit: SubmitHandler<CatalogCustomer> = async (
		data: CatalogCustomer,
	) => {
		if (modal === "delete" && data.CustomerId) {
			deleteMutation.mutate(data.CustomerId);
		} else {
			upsertMutation.mutate(data);
		}
	};

	useEffect(() => {
		if (modal !== "") {
			reset(
				modal === "create"
					? CATALOG_CUSTOMER_INITIAL_VALUES
					: customer || CATALOG_CUSTOMER_INITIAL_VALUES,
			);
			setTitle(
				modal === "create" ? "CREATE" : modal === "update" ? "EDIT" : "DELETE",
			);
		}
	}, [modal, customer, reset]);

	return (
		<>
			{(modal === "create" || modal === "update" || modal === "delete") && (
				<HelmetDexteel
					title={`${
						modal === "create"
							? "New"
							: `${modal === "update" ? "Edit" : "Delete"}`
					} Customer - Customers`}
				/>
			)}
			<MesfModal
				id="modal-customer"
				title={`${title} CUSTOMER`}
				open={modal !== ""}
				handleClose={() => onHide(false)}
				maxWidth="sm"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content style={{ padding: "15px 30px" }}>
						<Grid2 container spacing={2}>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<Controller
									name="CustomerName"
									control={control}
									rules={{
										required: "Name is required",
									}}
									render={({ field, fieldState: { error } }) => (
										<TextField
											label="Name"
											variant="outlined"
											value={field.value || ""}
											onChange={field.onChange}
											error={!!error}
											helperText={error?.message}
											fullWidth
											autoComplete="off"
											size="small"
											disabled={modal === "delete"}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={
								deleteMutation.isPending || upsertMutation.isPending
							}
							onHide={onHide}
							buttonLabel={modal === "delete" ? "Delete" : "Save"}
							buttonColor={modal === "delete" ? "secondary" : "primary"}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
